import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Select as AntdSelect,
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Space
} from 'antd';
import dayjs from 'dayjs';
import { capitalize, isObject, map } from 'lodash';
import moment from 'moment-timezone';
import { CalendarBlank } from 'phosphor-react';
import React, { useEffect, useState } from 'react';
import * as urlSlug from 'url-slug';
import {
  ASSET_CATEGORY,
  MAX_LENGTHS,
  MODULES,
  PAGE_TYPES,
  ROUTES,
  STATUS_OPTIONS,
  STATUS_SCHEDULED,
  STATUS_TYPES,
  UNPUBLISHED_STATUS,
  VIDEO_LIVE_STATUS,
  VIDEO_LIVE_STATUS_OPTIONS,
  VIDEO_RESOURCES_TYPES,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION,
  defaultDateFormat,
  defaultDateFormatWithTime
} from '../../common/constants';
import PageHeader from '../../components/PageHeader';
import { GET_TAGS, GET_TOPICS, GET_VIDEO } from './graphql/Queries';

import AudioPlaceholder from '../../assets/images/audioPlaceholder.jpg';
import { formValidatorRules } from '../../common/utils';
import AllowedPlansTable from '../../components/AllowedPlansTable';
import HtmlEditor from '../../components/HtmlEditor';
import useCheckPermission from '../../hooks/useCheckPermission';
import SelectAsset, { initialAsset } from '../assets/components/SelectAsset';
import { SlugInput } from '../labels/topics/components/FormInputs';
import { GET_PAGE_TEMPLATES } from '../page-templates/graphql/Queries';
import {
  Permissions,
  Switch
} from '../pages/component/pageModules/moduleForms/FormInputs';
import { Select } from './components/FormInputs';
import { CREATE_VIDEO, UPDATE_VIDEO } from './graphql/Mutations';

const initialResources = {
  [VIDEO_RESOURCES_TYPES.QUESTIONS]: '',
  [VIDEO_RESOURCES_TYPES.LINKS]: ''
};

const initialValues = {
  title: '',
  description: '',
  slug: '/',
  tags: [],
  topics: [],
  status: STATUS_TYPES.DRAFT,
  contentRating: '',
  video: {
    ...initialAsset,
    isLive: false,
    status: null
  },
  image: {
    ...initialAsset
  },
  imageThumbnail: {
    ...initialAsset
  },
  videoThumbnail: {
    ...initialAsset
  },
  timelineThumbnail: {
    ...initialAsset
  },
  videoAsset: {
    ...initialAsset
  },
  documents: [],
  audios: [],
  tracks: [],
  metaHeader: '',
  metaFooter: '',
  resources: {
    ...initialResources
  },
  html: '',
  permissions: [],
  liveStatus: VIDEO_LIVE_STATUS.PENDING,
  isLive: false,
  liveUrl: '',
  lowResUrl: '',
  pageTemplate: null
};

const resources = [
  { label: 'Questions', name: VIDEO_RESOURCES_TYPES.QUESTIONS },
  { label: 'Links', name: VIDEO_RESOURCES_TYPES.LINKS }
];

const VIDEO_DESCRIPTION_MAX_LENGTH = 2000;

const TIMEZONE_OPTIONS = moment.tz.names().map((timezone) => ({
  label: `${moment.tz(timezone).format('([GMT]Z)')} ${timezone}`,
  value: timezone
}));

const validateLiveStatusTransition = (newStatus, currentStatus) => {
  const getStatusLabel = (statusValue) => {
    const option = VIDEO_LIVE_STATUS_OPTIONS.find(
      (opt) => opt.value === statusValue
    );
    return option ? option.label : statusValue;
  };

  if (!currentStatus) {
    return newStatus === VIDEO_LIVE_STATUS.PENDING
      ? Promise.resolve()
      : Promise.reject(
          new Error(
            `New live videos must start with ${getStatusLabel(
              VIDEO_LIVE_STATUS.PENDING
            )} status`
          )
        );
  }

  // valid transitions
  const validTransitions = {
    [VIDEO_LIVE_STATUS.PENDING]: [
      VIDEO_LIVE_STATUS.PENDING,
      VIDEO_LIVE_STATUS.LIVE,
      VIDEO_LIVE_STATUS.PAUSED,
      VIDEO_LIVE_STATUS.COMPLETED
    ],
    [VIDEO_LIVE_STATUS.LIVE]: [
      VIDEO_LIVE_STATUS.PENDING,
      VIDEO_LIVE_STATUS.LIVE,
      VIDEO_LIVE_STATUS.PAUSED,
      VIDEO_LIVE_STATUS.COMPLETED
    ],
    [VIDEO_LIVE_STATUS.PAUSED]: [
      VIDEO_LIVE_STATUS.PENDING,
      VIDEO_LIVE_STATUS.LIVE,
      VIDEO_LIVE_STATUS.PAUSED,
      VIDEO_LIVE_STATUS.COMPLETED
    ],
    [VIDEO_LIVE_STATUS.VOD_PROCESSING]: [VIDEO_LIVE_STATUS.VOD_PROCESSING],
    [VIDEO_LIVE_STATUS.COMPLETED]: [
      VIDEO_LIVE_STATUS.COMPLETED,
      VIDEO_LIVE_STATUS.VOD_PROCESSING
    ],
    [VIDEO_LIVE_STATUS.VOD_READY]: [VIDEO_LIVE_STATUS.VOD_READY]
  };

  const allowedTransitions = validTransitions[currentStatus] || [];

  if (allowedTransitions.includes(newStatus)) {
    return Promise.resolve();
  }

  // Generate error message dynamically from validTransitions
  const currentAllowedTransitions = validTransitions[currentStatus] || [];
  const allowedLabels = currentAllowedTransitions
    .filter((status) => status !== currentStatus)
    .map(getStatusLabel);

  let errorMessage = '';
  if (allowedLabels.length === 0) {
    errorMessage = `${getStatusLabel(
      currentStatus
    )} status cannot be changed to any other status`;
  } else {
    const allowedList = allowedLabels.join(', ');
    errorMessage = `From ${getStatusLabel(
      currentStatus
    )} status, you can only change to ${allowedList}`;
  }

  return Promise.reject(new Error(errorMessage));
};

const getLiveStatusOptions = (currentStatus) => {
  const shouldLockToVodReady = currentStatus === VIDEO_LIVE_STATUS.VOD_READY;
  return VIDEO_LIVE_STATUS_OPTIONS.map(({ label, value }) => ({
    label,
    value,
    disabled: shouldLockToVodReady
      ? value !== VIDEO_LIVE_STATUS.VOD_READY
      : value === VIDEO_LIVE_STATUS.VOD_READY
  }));
};

const AddEditVideo = ({ history, match: { params } }) => {
  const [form] = Form.useForm();
  const { videoId } = params;
  const isEdit = !!videoId;
  const [originalLiveStatus, setOriginalLiveStatus] = useState(null);

  const isLive = Form.useWatch('isLive', form);
  const currentLiveStatus = Form.useWatch('liveStatus', form);

  const [
    fetchVideoDetails,
    { loading: fetchingDetails, data: { videoAdmin } = {} }
  ] = useLazyQuery(GET_VIDEO, {
    fetchPolicy: 'network-only'
  });

  const [addUpdateVideo, { loading }] = useMutation(
    isEdit ? UPDATE_VIDEO : CREATE_VIDEO
  );

  const handleChangeDate = (event) => {
    if (event) {
      form.setFieldsValue({
        status: capitalize(STATUS_SCHEDULED)
      });
    } else {
      form.setFieldsValue({
        status: STATUS_TYPES.DRAFT
      });
    }
  };

  useEffect(() => {
    if (isEdit && !!videoId) {
      fetchVideoDetails({
        variables: {
          id: videoId
        }
      }).then((res) => {
        const video = res.data?.videoAdmin;
        if (video) {
          const slug = video?.slug.startsWith('/')
            ? video?.slug
            : `/${video?.slug}`;
          form.setFieldsValue({
            title: video?.title ?? '',
            description: video?.description ?? '',
            slug,
            displayDate: video?.displayDate ? dayjs(video?.displayDate) : null,
            autoPublishAt: video?.autoPublishAt
              ? dayjs(video?.autoPublishAt)
              : null,
            permissions:
              video?.permissions?.map((value) => ({
                label: value,
                value
              })) ?? [],
            tags:
              video?.tags?.map((tag) => ({
                label: tag?.name,
                value: tag?.id
              })) ?? [],
            topics:
              video?.topics?.length > 0
                ? map(video?.topics, (item) => {
                    return {
                      value: item?.id,
                      label: item?.name
                    };
                  })
                : [],
            status:
              video?.status === STATUS_SCHEDULED
                ? capitalize(STATUS_SCHEDULED)
                : video?.status ?? STATUS_TYPES.DRAFT,
            contentRating: video?.contentRating ?? '',
            liveStatus: video?.liveStatus ?? '',
            liveInfo: {
              timezone: video?.liveInfo?.timezone ?? '',
              startDate: video?.liveInfo?.startDate
                ? dayjs(video?.liveInfo?.startDate)
                : '',
              endDate: video?.liveInfo?.endDate
                ? dayjs(video?.liveInfo?.endDate)
                : ''
            },
            video: {
              id: video?.video?.id ?? '',
              url:
                video?.video?.serviceVideoThumbnail ??
                video?.video?.serviceImageThumbnail ??
                '',
              status: video?.video?.status
            },
            previewVideo: video?.previewVideo
              ? {
                  id: video?.previewVideo?.id,
                  url:
                    video?.previewVideo?.serviceVideoThumbnail ??
                    video?.previewVideo?.serviceImageThumbnail ??
                    '',
                  title: video?.previewVideo?.title
                }
              : {},
            image: {
              id: video?.image?.id || '',
              url: video?.image?.url || ''
            },
            imageThumbnail: {
              id: video?.imageThumbnail?.id || '',
              url: video?.imageThumbnail?.url || ''
            },
            videoThumbnail: {
              id: video?.videoThumbnail?.id ?? '',
              url: video?.videoThumbnail?.url ?? ''
            },
            timelineThumbnail: {
              id: video?.timelineThumbnail?.id ?? '',
              url: video?.timelineThumbnail?.url ?? ''
            },
            documents:
              video?.documents?.map(({ id, url, title }) => ({
                id,
                url,
                title
              })) ?? [],
            html: video?.html ?? '',
            audios:
              video?.audioTracks?.map(
                ({ id, serviceImageThumbnail, title }) => ({
                  id,
                  url: serviceImageThumbnail ?? AudioPlaceholder,
                  title
                })
              ) ?? [],
            tracks:
              video?.textTracks?.map(({ id, url, title }) => ({
                id,
                url,
                title
              })) ?? [],
            metaHeader: video?.metaHeader ?? '',
            metaFooter: video?.metaFooter ?? '',
            resources: (video?.resources ?? [])?.reduce(
              (acc, data) => {
                if (data?.content) {
                  acc[VIDEO_RESOURCES_TYPES[data?.type]] = data?.content;
                }
                return acc;
              },
              { ...initialResources }
            ),
            allowedPlans:
              video?.allowedPlans?.map((item) => ({
                ...item,
                key: { label: item?.key, value: item?.key },
                expiredAt: item?.expiredAt ? dayjs(item?.expiredAt) : null
              })) ?? [],
            pageTemplate: video?.pageTemplate
              ? {
                  label: video?.pageTemplate?.templateName,
                  value: video?.pageTemplate?.id
                }
              : null
          });
          setOriginalLiveStatus(video?.liveStatus);
        }
      });
    }
  }, [isEdit, videoId, form, fetchVideoDetails]);

  const handleCancel = () => {
    history.replace(ROUTES.VIDEOS_MODULE);
  };

  const formatPayload = (data) => {
    const payload = {
      title: data?.title || '',
      description: data?.description || '',
      autoPublishAt:
        data?.autoPublishAt && dayjs(data?.autoPublishAt).toISOString(),
      displayDate: data?.displayDate && dayjs(data?.displayDate).toISOString(),
      slug: data?.slug?.startsWith('/') ? data?.slug?.substring(1) : data?.slug,
      tags:
        data?.tags?.map((item, index) => {
          return {
            tagId: item?.value,
            order: index + 1
          };
        }) || [],
      topics:
        data?.topics?.length > 0
          ? map(data?.topics, (item, index) => {
              return {
                topicId: item?.value,
                order: index + 1
              };
            })
          : [],
      status:
        data?.status === capitalize(STATUS_SCHEDULED) ? null : data?.status,
      contentRating: data?.contentRating || '',
      imageId: data?.image?.id || null,
      imageThumbnailId: data?.imageThumbnail?.id || null,
      videoThumbnailId: data?.videoThumbnail?.id || null,
      timelineThumbnailId: data?.timelineThumbnail?.id || null,
      videoAsset: {
        id: data?.video?.id || null,
        previewId: data?.previewVideo?.id || null
      },
      audioTrackIds: data?.audios?.map(({ id }) => id),
      textTrackIds: data?.tracks?.map(({ id }) => id),
      html: data?.html ?? '',
      documentIds: data?.documents?.map(({ id }) => id),
      metaHeader: data?.metaHeader || '',
      metaFooter: data?.metaFooter || '',
      resources: Object.entries(VIDEO_RESOURCES_TYPES).map(([key, name]) => ({
        type: key,
        content: data?.resources?.[name] ?? ''
      })),
      permissions: data?.permissions?.map(({ value }) => value),
      pageTemplateId: data?.pageTemplate?.value || null,
      allowedPlans: data?.allowedPlans?.map(({ key, expiredAt }) => ({
        key: isObject(key) ? key?.value : key,
        expiredAt: expiredAt ? dayjs(expiredAt) : null
      })),
      liveStatus: data?.liveStatus || VIDEO_LIVE_STATUS.PENDING,
      ...(!isEdit && {
        isLive: data?.video?.isLive ?? false
      }),
      ...(isEdit &&
        videoAdmin?.isLive && {
          liveUrl: data?.liveUrl,
          liveInfo: {
            timezone: data?.liveInfo?.timezone,
            startDate: data?.liveInfo?.startDate,
            endDate: data?.liveInfo?.endDate
          }
        }),
      ...(data?.isLive && {
        isLive: true,
        liveUrl: data?.liveUrl,
        lowResUrl: data?.lowResUrl,
        liveInfo: {
          timezone: data?.liveInfo?.timezone,
          startDate: data?.liveInfo?.startDate,
          endDate: data?.liveInfo?.endDate
        }
      })
    };
    return payload;
  };

  const handleSubmit = async () => {
    try {
      await form?.validateFields();
      const data = form?.getFieldsValue();
      const payload = formatPayload(data);
      addUpdateVideo({
        variables: {
          data: payload,
          ...(isEdit && {
            id: videoId
          })
        },
        onCompleted() {
          history.push(ROUTES?.VIDEOS_MODULE);
        }
      });
    } catch {
      //
    }
  };

  const handleTitleChange = (e) => {
    form.setFieldValue('slug', `/${urlSlug.convert(e.target.value)}`);
  };

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.CONTENT_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.CONTENT_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const noLiveAssets =
    typeof isLive === 'boolean' ? isLive : videoAdmin?.isLive;

  return (
    <>
      <PageHeader menu={MODULES?.CONTENT_UNITS} />
      <div className="page-wrapper">
        <div className="page-wrapper-body">
          <Form
            form={form}
            className="add-edit-form"
            layout="vertical"
            initialValues={initialValues}
            onFinish={handleSubmit}
            disabled={isViewOnly || fetchingDetails}
            validateTrigger={['onChange', 'onBlur']}
          >
            <Row gutter={[16, 0]}>
              <Col md={24} lg={12}>
                <Form.Item
                  label="Title"
                  name="title"
                  required
                  rules={[
                    formValidatorRules?.required('Please enter title!'),
                    formValidatorRules?.maxLength(MAX_LENGTHS.TITLE)
                  ]}
                >
                  <Input
                    placeholder="Enter title"
                    onChange={handleTitleChange}
                  />
                </Form.Item>
                <Form.Item
                  name="description"
                  label="Description"
                  rules={[
                    formValidatorRules?.maxLength(VIDEO_DESCRIPTION_MAX_LENGTH)
                  ]}
                >
                  <Input.TextArea placeholder="Enter description" />
                </Form.Item>
                <Form.Item
                  label="Slug"
                  name="slug"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter slug!'
                    },
                    formValidatorRules?.maxLength(MAX_LENGTHS.TITLE)
                  ]}
                >
                  <SlugInput />
                </Form.Item>
                <Form.Item name="tags" label="Tags">
                  <Select
                    mode="multiple"
                    placeholder="Select tags"
                    query={GET_TAGS}
                    variablesSelector={(filter) => ({ filter })}
                    dataSelector={(data) =>
                      data?.tagsAdmin?.tags?.map(({ id, name }) => ({
                        label: name,
                        value: id
                      })) ?? []
                    }
                    keys={{
                      data: 'tagsAdmin',
                      records: 'tags',
                      count: 'count'
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="topics"
                  label="Topic"
                  required
                  rules={[
                    {
                      async validator(_, value) {
                        if (!value?.length) {
                          throw new Error('Please select topic!');
                        }
                      }
                    }
                  ]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select topic"
                    query={GET_TOPICS}
                    variablesSelector={(filter) => ({ filter })}
                    dataSelector={(data) =>
                      data?.topicsAdmin?.topics?.map(({ id, name }) => ({
                        label: name,
                        value: id
                      })) ?? 0
                    }
                    keys={{
                      data: 'topicsAdmin',
                      records: 'topics',
                      count: 'count'
                    }}
                  />
                </Form.Item>
                <Form.Item label="Status" name="status">
                  <AntdSelect
                    options={[...STATUS_OPTIONS, UNPUBLISHED_STATUS].map(
                      ({ name, value }) => ({
                        label: name,
                        value
                      })
                    )}
                    placeholder="Select status"
                  />
                </Form.Item>
                <Form.Item
                  name="contentRating"
                  label="Content Rating"
                  rules={[
                    formValidatorRules?.maxLength(MAX_LENGTHS.DESCRIPTION)
                  ]}
                >
                  <Input.TextArea placeholder="Enter content rating" />
                </Form.Item>
                <Form.Item name="pageTemplate" label="Page Template">
                  <Select
                    placeholder="Select page template"
                    query={GET_PAGE_TEMPLATES}
                    variablesSelector={({ limit, skip, search }) => ({
                      filter: {
                        limit,
                        skip,
                        search,
                        type: PAGE_TYPES.VIDEO,
                        status: STATUS_TYPES.PUBLISHED
                      }
                    })}
                    dataSelector={(data) =>
                      data?.pageTemplates?.pages?.map(
                        ({ id, templateName }) => ({
                          label: templateName,
                          value: id
                        })
                      ) ?? []
                    }
                    keys={{
                      data: 'pageTemplates',
                      records: 'pages',
                      count: 'count'
                    }}
                    allowClear
                  />
                </Form.Item>
                <>
                  {!isEdit && (
                    <Form.Item name="isLive" valuePropName="checked">
                      <Switch label="Do you have a livestream video URL?" />
                    </Form.Item>
                  )}
                  {videoAdmin?.isLive && (
                    <>
                      <Form.Item
                        label="Live Status"
                        name="liveStatus"
                        rules={[
                          {
                            validator: (_, value) => {
                              return validateLiveStatusTransition(
                                value,
                                originalLiveStatus
                              );
                            }
                          }
                        ]}
                      >
                        <AntdSelect
                          options={getLiveStatusOptions(
                            originalLiveStatus ?? currentLiveStatus
                          )}
                          placeholder="Select live status"
                        />
                      </Form.Item>
                      <Form.Item
                        label="Live Timezone"
                        name={['liveInfo', 'timezone']}
                      >
                        <AntdSelect
                          showSearch
                          options={TIMEZONE_OPTIONS}
                          placeholder="Select timezone"
                          popupMatchSelectWidth={false}
                          onChange={() => {
                            form?.validateFields([['schedule', 'startTime']]);
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Live stream start Date/Time"
                        name={['liveInfo', 'startDate']}
                        rules={[
                          {
                            async validator(_, value) {
                              if (!value)
                                return Promise.reject(
                                  new Error('Please select a start date/time')
                                );
                              if (dayjs().isAfter(value)) {
                                return Promise.reject(
                                  new Error('Start time cannot be in the past')
                                );
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <DatePicker
                          showTime
                          placeholder="Enter as Central Time"
                          disabledDate={(day) =>
                            dayjs().startOf('day').isAfter(day)
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        label="Live stream end Date/Time"
                        name={['liveInfo', 'endDate']}
                        rules={[
                          {
                            async validator(_, value) {
                              const startDate = form.getFieldValue([
                                'liveInfo',
                                'startDate'
                              ]);
                              const start = dayjs(startDate);
                              const end = dayjs(value);
                              if (!start.isValid() || !end.isValid()) {
                                return Promise.reject(
                                  new Error('Invalid datetime selected')
                                );
                              }
                              if (!end.isAfter(start)) {
                                return Promise.reject(
                                  new Error(
                                    'End time must be after start time, even on same day'
                                  )
                                );
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <DatePicker
                          showTime
                          placeholder="Enter as Central Time"
                          disabledDate={(day) =>
                            dayjs().startOf('day').isAfter(day)
                          }
                        />
                      </Form.Item>
                    </>
                  )}
                  {isLive ? (
                    <>
                      <Form.Item
                        name="liveUrl"
                        label='Live Stream HLS URL "M3U8"'
                        required
                        rules={[
                          formValidatorRules?.required('Please enter URL!'),
                          {
                            type: 'url',
                            message: 'Please enter valid URL!'
                          }
                        ]}
                      >
                        <Input type="url" placeholder="Enter url" />
                      </Form.Item>
                      <Form.Item
                        name="lowResUrl"
                        label="Low Res URL"
                        rules={[
                          {
                            type: 'url',
                            message: 'Please enter valid URL!'
                          }
                        ]}
                      >
                        <Input type="url" placeholder="Enter low res url" />
                      </Form.Item>
                      <Form.Item
                        label="Live Status"
                        name="liveStatus"
                        rules={[
                          {
                            validator: (_, value) => {
                              const currentStatus = form.getFieldValue(
                                'liveStatus'
                              );
                              return validateLiveStatusTransition(
                                value,
                                currentStatus
                              );
                            }
                          }
                        ]}
                      >
                        <AntdSelect
                          options={getLiveStatusOptions(
                            currentLiveStatus ?? originalLiveStatus
                          )}
                          placeholder="Select live status"
                        />
                      </Form.Item>
                      <Form.Item
                        label="Live Timezone"
                        name={['liveInfo', 'timezone']}
                        rules={[
                          {
                            validator(_, value) {
                              if (!value) {
                                return Promise.reject(
                                  new Error('Please select at timezone!')
                                );
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <AntdSelect
                          showSearch
                          options={TIMEZONE_OPTIONS}
                          placeholder="Select timezone"
                          popupMatchSelectWidth={false}
                          onChange={() => {
                            form?.validateFields([['schedule', 'startTime']]);
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Live stream start Date/Time"
                        name={['liveInfo', 'startDate']}
                        rules={[
                          {
                            async validator(_, value) {
                              if (!value)
                                return Promise.reject(
                                  new Error('Please select a start date/time')
                                );
                              if (dayjs().isAfter(value)) {
                                return Promise.reject(
                                  new Error('Start time cannot be in the past')
                                );
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <DatePicker
                          showTime
                          placeholder="Enter as Central Time"
                          disabledDate={(day) =>
                            dayjs().startOf('day').isAfter(day)
                          }
                          onChange={() => {
                            form?.validateFields([['liveInfo', 'timezone']]);
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        label="Live stream end Date/Time"
                        name={['liveInfo', 'endDate']}
                        rules={[
                          {
                            async validator(_, value) {
                              const startDate = form.getFieldValue([
                                'liveInfo',
                                'startDate'
                              ]);
                              const start = dayjs(startDate);
                              const end = dayjs(value);
                              if (!start.isValid() || !end.isValid()) {
                                return Promise.reject(
                                  new Error('Invalid datetime selected')
                                );
                              }
                              if (!end.isAfter(start)) {
                                return Promise.reject(
                                  new Error(
                                    'End time must be after start time, even on same day'
                                  )
                                );
                              }
                              return Promise.resolve();
                            }
                          }
                        ]}
                      >
                        <DatePicker
                          showTime
                          placeholder="Enter as Central Time"
                          disabledDate={(day) =>
                            dayjs().startOf('day').isAfter(day)
                          }
                        />
                      </Form.Item>
                    </>
                  ) : (
                    <>
                      <Form.Item
                        label="Video"
                        name="video"
                        required
                        rules={[
                          {
                            async validator(_, value) {
                              if (value.id === '') {
                                throw new Error('Please select video!');
                              }
                            }
                          }
                        ]}
                      >
                        <SelectAsset
                          disabled={isViewOnly}
                          modalTitle="Select Video"
                          categoryKey={ASSET_CATEGORY.VIDEO}
                          btnText="Video"
                          dataSelector={({
                            id,
                            serviceImageThumbnail,
                            serviceVideoThumbnail
                          }) => ({
                            id,
                            url: serviceVideoThumbnail || serviceImageThumbnail
                          })}
                        />
                      </Form.Item>
                    </>
                  )}
                </>

                <Form.Item label="Preview Video" name="previewVideo">
                  <SelectAsset
                    disabled={isViewOnly}
                    modalTitle="Select Preview Video"
                    categoryKey={ASSET_CATEGORY.VIDEO}
                    btnText="Preview Video"
                    key={noLiveAssets}
                    noLive={noLiveAssets}
                    dataSelector={({
                      id,
                      serviceImageThumbnail,
                      serviceVideoThumbnail
                    }) => ({
                      id,
                      url: serviceVideoThumbnail || serviceImageThumbnail
                    })}
                  />
                </Form.Item>
                <Form.Item
                  label="Image"
                  name="image"
                  extra="Recommended size  (2500 * 1242)"
                >
                  <SelectAsset
                    disabled={isViewOnly}
                    modalTitle="Select Image"
                    categoryKey={ASSET_CATEGORY.IMAGE}
                    btnText="Image"
                    dataSelector={({ id, url }) => ({
                      id,
                      url
                    })}
                  />
                </Form.Item>
                <Form.Item
                  label="Image Thumbnail"
                  name="imageThumbnail"
                  extra="Recommended size  (590 * 330)"
                >
                  <SelectAsset
                    disabled={isViewOnly}
                    modalTitle="Select Image Thumbnail"
                    categoryKey={ASSET_CATEGORY.IMAGE}
                    btnText="Image Thumbnail"
                    dataSelector={({ id, url }) => ({
                      id,
                      url
                    })}
                  />
                </Form.Item>
                <Form.Item
                  label="Video Thumbnail"
                  name="videoThumbnail"
                  extra="Recommended size  (590 * 330)"
                >
                  <SelectAsset
                    disabled={isViewOnly}
                    modalTitle="Select Video Thumbnail"
                    categoryKey={ASSET_CATEGORY.IMAGE}
                    btnText="Video Thumbnail"
                    dataSelector={({ id, url }) => ({
                      id,
                      url
                    })}
                  />
                </Form.Item>
                {/* NOTE: Commented out for CVP 7.2 fixes - BBART-2082
                    See: https://app.clickup.com/t/606124/BBART-2082
                <Form.Item label="Text Tracks" name="tracks">
                  <SelectAsset
                    disabled={isViewOnly}
                    multiple
                    modalTitle="Select Tracks"
                    btnText="Tracks"
                    categoryKey={ASSET_CATEGORY.TEXT}
                    dataSelector={({ id, url, title }) => ({
                      id,
                      url,
                      title
                    })}
                  />
                </Form.Item>
                */}
                <Form.Item label="Audios" name="audios">
                  <SelectAsset
                    disabled={isViewOnly}
                    multiple
                    modalTitle="Select Audios"
                    btnText="Audios"
                    categoryKey={ASSET_CATEGORY.AUDIO}
                    dataSelector={({ id, serviceImageThumbnail, title }) => ({
                      id,
                      url: serviceImageThumbnail,
                      title
                    })}
                  />
                </Form.Item>
                <Form.Item label="Documents" name="documents">
                  <SelectAsset
                    disabled={isViewOnly}
                    multiple
                    modalTitle="Select Documents"
                    btnText="Documents"
                    categoryKey={ASSET_CATEGORY.DOCUMENT}
                    dataSelector={({ id, url, title }) => ({
                      id,
                      url,
                      title
                    })}
                  />
                </Form.Item>

                {resources.map(({ label, name }) => (
                  <Form.Item
                    key={name}
                    name={['resources', name]}
                    label={label}
                    rules={[
                      formValidatorRules?.maxLength(
                        4000,
                        'Max length of 4000 character exceeded'
                      )
                    ]}
                  >
                    <HtmlEditor disabled={isViewOnly} className="full-html" />
                  </Form.Item>
                ))}
                <Form.Item label="Display Date" name="displayDate">
                  <DatePicker
                    suffixIcon={<CalendarBlank size={20} />}
                    format={defaultDateFormat}
                  />
                </Form.Item>
                <Form.Item label="Auto Publish On" name="autoPublishAt">
                  <DatePicker
                    showTime
                    suffixIcon={<CalendarBlank size={20} />}
                    disabledDate={(day) => dayjs().isAfter(day, 'day')}
                    format={defaultDateFormatWithTime}
                    onChange={handleChangeDate}
                  />
                </Form.Item>

                <Form.Item
                  name="metaHeader"
                  label="Meta Header"
                  rules={[
                    formValidatorRules?.maxLength(MAX_LENGTHS.DESCRIPTION)
                  ]}
                >
                  <Input.TextArea rows={5} placeholder="Enter meta header" />
                </Form.Item>

                <Form.Item
                  name="metaFooter"
                  label="Meta Footer"
                  rules={[
                    formValidatorRules?.maxLength(MAX_LENGTHS.DESCRIPTION)
                  ]}
                >
                  <Input.TextArea rows={5} placeholder="Enter meta footer" />
                </Form.Item>
                <Permissions />
                <fieldset className="mb-12">
                  <legend className="role-legend">Allowed Plans</legend>
                  <AllowedPlansTable
                    loading={fetchingDetails}
                    form={form}
                    formKey="allowedPlans"
                  />
                </fieldset>
              </Col>
              <Col md={24} lg={12}>
                <Form.Item className="full-html" label="Html" name="html">
                  <HtmlEditor disabled={isViewOnly} className="full-html" />
                </Form.Item>
              </Col>
            </Row>
            <div className="d-flex button-section mb-8">
              <Space>
                {isAddEditAllowed && (
                  <Button
                    disabled={loading || fetchingDetails}
                    loading={loading}
                    type="text"
                    htmlType="submit"
                    className="text-btn mr-8"
                    size="middle"
                  >
                    Save
                  </Button>
                )}

                <Button
                  disabled={loading}
                  type="text"
                  className="text-btn2"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Space>
            </div>
          </Form>
        </div>
      </div>
    </>
  );
};

export default AddEditVideo;
